import { MeshBuilder, Vector3, Mesh } from '@babylonjs/core';

// Meter-to-feet scale factor (all furniture dimensions are authored in meters)
const M = 3.28084;
const m = (v) => v * M;

/**
 * Procedural furniture factory for interior scenes.
 * All furniture is built from primitive shapes — no external models needed.
 * Internal dimensions are in meters, scaled to feet via group.scaling.
 */
export class FurnitureFactory {
  constructor(scene, materials) {
    this.scene = scene;
    this.mats = materials;
  }

  /**
   * Office desk with monitor, keyboard tray.
   */
  createDesk(name, x, y, z, rotation = 0, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Desktop surface
    const top = MeshBuilder.CreateBox(`${name}_top`, { width: 1.4, height: 0.04, depth: 0.7 }, this.scene);
    top.position = new Vector3(0, 0.74, 0);
    top.material = this.mats.wood;
    top.parent = group;

    // Legs (4 metal legs)
    for (const [lx, lz] of [[-0.65, -0.3], [0.65, -0.3], [-0.65, 0.3], [0.65, 0.3]]) {
      const leg = MeshBuilder.CreateBox(`${name}_leg_${lx}_${lz}`, { width: 0.04, height: 0.72, depth: 0.04 }, this.scene);
      leg.position = new Vector3(lx, 0.36, lz);
      leg.material = this.mats.chrome;
      leg.parent = group;
    }

    // Side panel (modesty panel)
    const panel = MeshBuilder.CreateBox(`${name}_panel`, { width: 1.3, height: 0.35, depth: 0.02 }, this.scene);
    panel.position = new Vector3(0, 0.53, 0.34);
    panel.material = this.mats.whitePlastic;
    panel.parent = group;

    // Monitor
    const monitorStand = MeshBuilder.CreateBox(`${name}_mstand`, { width: 0.08, height: 0.15, depth: 0.08 }, this.scene);
    monitorStand.position = new Vector3(0, 0.84, 0.1);
    monitorStand.material = this.mats.chrome;
    monitorStand.parent = group;

    const monitor = MeshBuilder.CreateBox(`${name}_monitor`, { width: 0.6, height: 0.38, depth: 0.03 }, this.scene);
    monitor.position = new Vector3(0, 1.1, 0.1);
    monitor.material = this.mats.darkAluminium;
    monitor.parent = group;

    // Screen (emissive)
    const screen = MeshBuilder.CreatePlane(`${name}_screen`, { width: 0.55, height: 0.33 }, this.scene);
    screen.position = new Vector3(0, 1.1, 0.085);
    screen.material = this.mats.lightPanel;
    screen.parent = group;

    // Keyboard
    const keyboard = MeshBuilder.CreateBox(`${name}_kb`, { width: 0.4, height: 0.02, depth: 0.14 }, this.scene);
    keyboard.position = new Vector3(0, 0.77, -0.1);
    keyboard.material = this.mats.darkAluminium;
    keyboard.parent = group;

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    group.rotation.y = rotation;
    return group;
  }

  /**
   * Office chair with wheels.
   */
  createChair(name, x, y, z, rotation = 0, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Base (5-star)
    const base = MeshBuilder.CreateCylinder(`${name}_base`, { diameter: 0.5, height: 0.03 }, this.scene);
    base.position = new Vector3(0, 0.03, 0);
    base.material = this.mats.chrome;
    base.parent = group;

    // Center pole
    const pole = MeshBuilder.CreateCylinder(`${name}_pole`, { diameter: 0.06, height: 0.38 }, this.scene);
    pole.position = new Vector3(0, 0.22, 0);
    pole.material = this.mats.chrome;
    pole.parent = group;

    // Seat
    const seat = MeshBuilder.CreateBox(`${name}_seat`, { width: 0.45, height: 0.06, depth: 0.42 }, this.scene);
    seat.position = new Vector3(0, 0.44, 0);
    seat.material = this.mats.fabricGray;
    seat.parent = group;

    // Backrest
    const back = MeshBuilder.CreateBox(`${name}_back`, { width: 0.43, height: 0.5, depth: 0.04 }, this.scene);
    back.position = new Vector3(0, 0.72, 0.19);
    back.material = this.mats.fabricGray;
    back.parent = group;

    // Armrests
    for (const side of [-1, 1]) {
      const arm = MeshBuilder.CreateBox(`${name}_arm_${side}`, { width: 0.04, height: 0.04, depth: 0.25 }, this.scene);
      arm.position = new Vector3(side * 0.22, 0.6, 0.05);
      arm.material = this.mats.darkAluminium;
      arm.parent = group;
    }

    // Casters (5 small wheels)
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const caster = MeshBuilder.CreateSphere(`${name}_caster_${i}`, { diameter: 0.04 }, this.scene);
      caster.position = new Vector3(Math.cos(angle) * 0.22, 0.02, Math.sin(angle) * 0.22);
      caster.material = this.mats.darkAluminium;
      caster.parent = group;
    }

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    group.rotation.y = rotation;
    return group;
  }

  /**
   * Reception desk (L-shaped, premium).
   */
  createReceptionDesk(name, x, y, z, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Front panel (tall, with gold accent strip)
    const front = MeshBuilder.CreateBox(`${name}_front`, { width: 3, height: 1.1, depth: 0.12 }, this.scene);
    front.position = new Vector3(0, 0.55, 0.5);
    front.material = this.mats.darkWood;
    front.parent = group;

    // Gold accent strip
    const goldStrip = MeshBuilder.CreateBox(`${name}_gold`, { width: 3, height: 0.03, depth: 0.13 }, this.scene);
    goldStrip.position = new Vector3(0, 1.05, 0.5);
    goldStrip.material = this.mats.goldAccent;
    goldStrip.parent = group;

    // Desktop
    const desktop = MeshBuilder.CreateBox(`${name}_desktop`, { width: 3, height: 0.05, depth: 0.8 }, this.scene);
    desktop.position = new Vector3(0, 0.76, 0.1);
    desktop.material = this.mats.marble;
    desktop.parent = group;

    // Side wing
    const wing = MeshBuilder.CreateBox(`${name}_wing`, { width: 0.12, height: 1.1, depth: 1.5 }, this.scene);
    wing.position = new Vector3(1.44, 0.55, -0.25);
    wing.material = this.mats.darkWood;
    wing.parent = group;

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    return group;
  }

  /**
   * Sofa (lobby/lounge).
   */
  createSofa(name, x, y, z, rotation = 0, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Base
    const base = MeshBuilder.CreateBox(`${name}_base`, { width: 2.0, height: 0.12, depth: 0.8 }, this.scene);
    base.position = new Vector3(0, 0.18, 0);
    base.material = this.mats.chrome;
    base.parent = group;

    // Seat cushion
    const seat = MeshBuilder.CreateBox(`${name}_seat`, { width: 1.9, height: 0.15, depth: 0.65 }, this.scene);
    seat.position = new Vector3(0, 0.32, -0.05);
    seat.material = this.mats.leather;
    seat.parent = group;

    // Back cushion
    const back = MeshBuilder.CreateBox(`${name}_back`, { width: 1.9, height: 0.45, depth: 0.15 }, this.scene);
    back.position = new Vector3(0, 0.52, 0.3);
    back.material = this.mats.leather;
    back.parent = group;

    // Arms
    for (const side of [-1, 1]) {
      const arm = MeshBuilder.CreateBox(`${name}_arm_${side}`, { width: 0.12, height: 0.3, depth: 0.7 }, this.scene);
      arm.position = new Vector3(side * 0.95, 0.42, 0);
      arm.material = this.mats.leather;
      arm.parent = group;
    }

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    group.rotation.y = rotation;
    return group;
  }

  /**
   * Ceiling light panel (recessed).
   */
  createCeilingLight(name, x, y, z, w = 1.2, d = 0.3, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Housing
    const housing = MeshBuilder.CreateBox(`${name}_housing`, { width: w, height: 0.04, depth: d }, this.scene);
    housing.position = new Vector3(0, 0, 0);
    housing.material = this.mats.whitePlastic;
    housing.parent = group;

    // Light surface (emissive)
    const light = MeshBuilder.CreatePlane(`${name}_glow`, { width: w - 0.04, height: d - 0.04 }, this.scene);
    light.position = new Vector3(0, -0.025, 0);
    light.rotation.x = Math.PI / 2;
    light.material = this.mats.lightPanel;
    light.parent = group;

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    return group;
  }

  /**
   * Display counter (showroom).
   */
  createDisplayCounter(name, x, y, z, w = 2, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    // Base cabinet
    const cabinet = MeshBuilder.CreateBox(`${name}_cabinet`, { width: w, height: 0.85, depth: 0.5 }, this.scene);
    cabinet.position = new Vector3(0, 0.425, 0);
    cabinet.material = this.mats.whitePlastic;
    cabinet.parent = group;

    // Glass top
    const glassTop = MeshBuilder.CreateBox(`${name}_glass`, { width: w, height: 0.35, depth: 0.5 }, this.scene);
    glassTop.position = new Vector3(0, 1.025, 0);
    glassTop.material = this.mats.glassInterior;
    glassTop.parent = group;

    // Counter top
    const counter = MeshBuilder.CreateBox(`${name}_counter`, { width: w + 0.04, height: 0.03, depth: 0.54 }, this.scene);
    counter.position = new Vector3(0, 1.21, 0);
    counter.material = this.mats.darkAluminium;
    counter.parent = group;

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    return group;
  }

  /**
   * Coffee table (lounge).
   */
  createCoffeeTable(name, x, y, z, parent = null) {
    const group = new Mesh(name, this.scene);
    group.parent = parent;

    const top = MeshBuilder.CreateCylinder(`${name}_top`, { diameter: 0.7, height: 0.03 }, this.scene);
    top.position = new Vector3(0, 0.42, 0);
    top.material = this.mats.marble;
    top.parent = group;

    const pole = MeshBuilder.CreateCylinder(`${name}_pole`, { diameter: 0.06, height: 0.4 }, this.scene);
    pole.position = new Vector3(0, 0.2, 0);
    pole.material = this.mats.chrome;
    pole.parent = group;

    const base = MeshBuilder.CreateCylinder(`${name}_base`, { diameter: 0.4, height: 0.02 }, this.scene);
    base.position = new Vector3(0, 0.01, 0);
    base.material = this.mats.chrome;
    base.parent = group;

    group.position = new Vector3(x, y, z);
    group.scaling = new Vector3(M, M, M);
    return group;
  }

  /**
   * Populate an office unit with furniture.
   */
  furnishOffice(unitId, x, y, z, w, d, parent) {
    // Desk in center-back
    this.createDesk(`${unitId}_desk`, x + w / 2, y, z + d * 0.65, 0, parent);
    // Chair behind desk
    this.createChair(`${unitId}_chair`, x + w / 2, y, z + d * 0.35, Math.PI, parent);
    // Visitor chair
    if (w > 16) {
      this.createChair(`${unitId}_vchair`, x + w / 2 - 2, y, z + d * 0.8, 0, parent);
    }
    // Ceiling lights
    const lightY = y + (d > 16 ? 9.2 : 8.9);
    this.createCeilingLight(`${unitId}_light1`, x + w / 2, lightY, z + d * 0.3, 1.0, 0.25, parent);
    this.createCeilingLight(`${unitId}_light2`, x + w / 2, lightY, z + d * 0.7, 1.0, 0.25, parent);
  }

  /**
   * Populate a showroom with display elements.
   */
  furnishShowroom(unitId, x, y, z, w, d, parent) {
    // Display counter along one wall
    if (w > 16) {
      this.createDisplayCounter(`${unitId}_counter`, x + w / 2, y, z + 1.3, Math.min(w - 3, 10), parent);
    }
    // Ceiling lights (row)
    const lightY = y + (d > 16 ? 13 : 8.9);
    const lightCount = Math.max(1, Math.floor(w / 10));
    for (let i = 0; i < lightCount; i++) {
      this.createCeilingLight(
        `${unitId}_light_${i}`,
        x + (i + 0.5) * (w / lightCount),
        lightY,
        z + d / 2,
        1.0, 0.25, parent
      );
    }
  }

  /**
   * Furnish reception area.
   */
  furnishReception(x, y, z, parent) {
    this.createReceptionDesk('reception_desk', x + 7, y, z + 16, parent);
    this.createSofa('reception_sofa1', x + 3, y, z + 5, 0, parent);
    this.createSofa('reception_sofa2', x + 10, y, z + 5, 0, parent);
    this.createCoffeeTable('reception_table', x + 7, y, z + 7, parent);

    // Ceiling lights
    for (let i = 0; i < 3; i++) {
      this.createCeilingLight(`reception_light_${i}`, x + 3 + i * 5, y + 23, z + 13, 1.2, 0.3, parent);
    }
  }

  /**
   * Furnish lounge area.
   */
  furnishLounge(x, y, z, w, d, parent) {
    this.createSofa('lounge_sofa1', x + w / 2, y, z + 3, 0, parent);
    this.createSofa('lounge_sofa2', x + w / 2, y, z + d - 3, Math.PI, parent);
    this.createCoffeeTable('lounge_table', x + w / 2, y, z + d / 2, parent);

    this.createCeilingLight('lounge_light', x + w / 2, y + 9.2, z + d / 2, 1.5, 0.4, parent);
  }
}
